import {useMemo, useRef} from "react";
import {TagDefinitionLike} from "../model/tag-definition";
import {EntityType} from "../model/entity";
import {TagSearchHit} from "../model/tag-assignment";
import {useDebouncedValue} from "@mantine/hooks";
import {useQueryTagsQuery} from "../store/entity/tag-assignment/tag-assignment-store";
import {extractErrorMessage} from "../util/error-util";
import {skipToken} from "@reduxjs/toolkit/query";
import {createSelector} from "@reduxjs/toolkit";
import {getUseQueryResultData} from "../util/selector-util";

type BuildTagSearchQueryOptions = {
  objectTypes?: EntityType[];
  freeText?: string;
}

const buildTagSearchQueryFromDefinitions = (
  tags: TagDefinitionLike[],
  options: BuildTagSearchQueryOptions = {}
): string => {
  const {objectTypes, freeText} = options;

  const parts: string[] = [];

  const normalizedObjectTypes = [...(objectTypes ?? [])]

  for (const objectType of normalizedObjectTypes) {
    parts.push(`target:${objectType}`);
  }

  for (const tag of tags) {
    if (!tag?.key) continue;
    parts.push(`#${tag.key}`);
  }

  if (freeText?.trim()) {
    parts.push(freeText.trim());
  }

  return parts.join(" ").trim();
}

export type UseTagDefinitionSearchQueryProps = {
  tagDefinitions?: TagDefinitionLike[];
  objectTypes?: EntityType[];
  freeText?: string;

  debounceMs?: number;
}

export type UseTagDefinitionSearchQueryResult = {
  objectIds?: string[]
  hits?: TagSearchHit[]
  isLoading?: boolean,
  error?: string,
}

export const useTagDefinitionSearchQuery = (props: UseTagDefinitionSearchQueryProps): UseTagDefinitionSearchQueryResult => {
  const {
    tagDefinitions,
    objectTypes,
    freeText,
    debounceMs = 250,
  } = props;

  const lastQueryRef = useRef<string | null>(null);

  const query = useMemo(() => {
    if (!tagDefinitions?.length) {
      return "";
    }

    return buildTagSearchQueryFromDefinitions(tagDefinitions, {
      objectTypes,
      freeText,
    });
  }, [tagDefinitions, objectTypes, freeText]);

  const [debouncedQuery] = useDebouncedValue(query, debounceMs);
  const trimmedQuery = useMemo(() => debouncedQuery.trim(), [debouncedQuery]);

  const skip = !trimmedQuery || !tagDefinitions?.length;

  const selector = useMemo(() => {
    return createSelector(
      [getUseQueryResultData<TagSearchHit[]>],
      (data) => {
        return data ?? []
      }
    );
  }, [])

  const {hits, isLoading, isFetching, error} = useQueryTagsQuery(skip ? skipToken : {
    query: trimmedQuery,
    objectTypes,
  }, {
    selectFromResult: (result) => ({
      ...result,
      hits: selector(result),
      isLoading: result.isLoading,
      isFetching: result.isFetching,
      error: result.error ? extractErrorMessage(result.error) : undefined,
    })
  });

  const objectIds = useMemo(() => {
    if (!hits || skip) {
      return undefined;
    }

    return Array.from(
      new Set(hits.map((hit) => hit.objectId).filter(Boolean))
    ) as string[];
  }, [hits, skip]);

  return {
    hits: hits,
    objectIds: objectIds,
    isLoading: isLoading,
    error: error
  };
};
